import { buildDocumentEndpointPath } from '@cs-cms/api-client';
import {
  CpsSelectedDocument,
  CPS_ACTIONS_ALLOWED_FOR_DOCUMENT,
  CONTENT_TYPE_FIELD,
  DOC_ID_FIELD,
  CPS_NOTIFICATION_TYPE
} from '@cs-cms/types';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentUserContext } from '../contexts/CurrentUserContext';
import { DocumentsContext } from '../contexts/DocumentsContext';
import { useGlobalizationContext } from '../contexts/GlobalizationContext';
import { useDownloadMany } from '../hooks/documents/useDownloadMany';
import { useTranslation } from '../hooks/globalization/useTranslation';
import { useNotificationContext } from '../hooks/useNotificationContext';

interface DocumentsProviderProps {
  children?: React.ReactNode;
  apiUrl: string;
  zipName: string;
}

export const DocumentsProvider: React.FC<DocumentsProviderProps> = ({ children, apiUrl, zipName }) => {
  const [selectedDocuments, setSelectedDocuments] = useState<CpsSelectedDocument[]>([]);
  const [openDocumentLoading, setOpenDocumentLoading] = useState<boolean>(false);
  const { addCenterNotification, showToast } = useNotificationContext();
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { mutate: downloadManyMutation } = useDownloadMany({ zipName });

  const openDocument = async (contentType: string, documentId: string) => {
    setOpenDocumentLoading(true);
    const redirectDocumentUrl = `${apiUrl}/${buildDocumentEndpointPath(contentType, documentId, selectedIndex?.id, true)}`;
    window.open(redirectDocumentUrl);
    setOpenDocumentLoading(false);
  };

  const downloadSingleFile = async () => {
    if (
      selectedDocuments[0] &&
      selectedDocuments[0].Cps_Permissions.includes(CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
    ) {
      const downloadUrl = `${apiUrl}/${buildDocumentEndpointPath(
        selectedDocuments[0][CONTENT_TYPE_FIELD],
        selectedDocuments[0][DOC_ID_FIELD],
        selectedIndex?.id,
        false
      )}`;

      window.open(downloadUrl, '_blank');
    }
  };

  const downloadFiles = async () => {
    switch (selectedDocuments.length) {
      case 0:
        break;
      case 1:
        await downloadSingleFile();

        addCenterNotification({
          id: uuidv4(),
          title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedTitle'),
          message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedMessage'),
          timestamp: new Date(),
          type: 'success'
        });

        showToast({
          type: CPS_NOTIFICATION_TYPE.SUCCESS,
          message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedTitle')
        });
        break;
      default: {
        const downloadableDocuments = selectedDocuments.filter(doc =>
          doc.Cps_Permissions.includes(CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
        );

        if (downloadableDocuments.length > 0 && selectedIndex?.id !== undefined) {
          const dto = {
            documents: downloadableDocuments.map(doc => ({
              docId: doc[DOC_ID_FIELD],
              contentType: doc[CONTENT_TYPE_FIELD]
            })),
            indexId: selectedIndex?.id
          };

          downloadManyMutation({ dto });
        }
        break;
      }
    }
  };

  return (
    <DocumentsContext.Provider
      value={{
        openDocumentLoading,
        selectedDocuments,
        setSelectedDocuments,
        openDocument,
        downloadFiles
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};
